import { Action } from '@reduxjs/toolkit';
import { AxiosProgressEvent } from 'axios';
import { push } from 'connected-react-router';
import { History, MemoryHistory } from 'history';
import { TFunction } from 'i18next';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import EventsClient from 'lib/clients/EventsClient';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { ApiEndpoint, ApiModel, ApiResponse, ApiSingletonPayload } from 'lib/interfaces/api';
import { DocumentConfig } from 'lib/interfaces/documentConfig';
import { captureException } from 'lib/reporting';
import { dataUrlToFile, PreparedFileUpload } from 'lib/utils/dataUrl';
import { assertNever } from 'lib/utils/types';
import { VerificationStatus } from 'lib/verification';
import SessionClientVerify from 'verify/lib/clients/SessionClientVerify';
import { clearMemoryHistory } from 'verify/lib/history';
import { StoredFileUpload } from 'verify/lib/interfaces/fileUploads';
import { DocumentGeneric, DocumentStatus } from 'verify/models/document';
import { Inquiry } from 'verify/models/inquiry';
import { VerificationDocument } from 'verify/models/verification';
import { clearUpload, setUploadProgress } from 'verify/redux/actions/document';
import { saveInquiry, updateDocumentAtIndex } from 'verify/redux/actions/inquiry';
import { showMessage } from 'verify/redux/actions/messages';
import { RootState } from 'verify/redux/store';
import { routeToNextStep } from 'verify/redux/thunk-actions/inquiry';
import {
  MAX_VERIFICATION_POLLS,
  verificationDocumentSubmit,
} from 'verify/redux/thunk-actions/verification';
import { DocumentStepAction } from 'verify/redux/types/document';
import { getDocumentStepUri } from 'verify/redux/utils/document';

export const IMAGE_TYPES = ['image/jpeg', 'image/png'];

export const POLL_INTERVAL = 1000;
const MAX_RETRIES = 3;

// Arbitrarily picked, should be canceled before this
const MAX_DOCUMENT_POLLS = 90;

const POLLS_BEFORE_REDIRECT = 3;

/**
 * @name routeToNextStepFromDocument
 *
 * Called upon polling success. Intelligently routes to the next document step
 * based on what documents have been already uploaded.
 */
export function routeToNextStepFromDocument(
  inquiry: Inquiry,
  documentsToVerify: DocumentConfig[],
  currentDocumentNumber: number,
): ThunkAction<void, RootState, void, Action> {
  return (dispatch: ThunkDispatch<RootState, void, Action>) => {
    if (currentDocumentNumber + 1 >= documentsToVerify.length) {
      dispatch(routeToNextStep(inquiry));
    } else {
      // Update inquiry with latest state before proceeding to next document.
      dispatch(saveInquiry(inquiry));
      dispatch(push(getDocumentStepUri(currentDocumentNumber + 1, DocumentStepAction.Prompt)));
    }
  };
}

interface CreateDocumentGenericWithVerificationArgs {
  stepName: string;
  documentsToVerify: DocumentConfig[];
  documentNumber: number;
  history: History;
  fileToUpload: StoredFileUpload;
  t: TFunction;
  forcedStatus?: VerificationStatus;
}

interface CreateDocumentGenericAttributes extends Pick<DocumentGeneric['attributes'], 'kind'> {
  files: PreparedFileUpload[];
}

/**
 * @name createDocumentGenericWithVerification
 *
 * 1. convert data URLs to files and make a partial `DocumentGeneric` object
 * 2. attempt to create said document with retry; if failure, early exit
 * 3. submit created document
 * 4. set document in redux store for UI
 * 5. poll for document processing status
 * 6. when document is processed successfully, create/submit/poll for a document verification.
 *
 * When polling for document status, there are 3 front-end states:
 * 1. pending -- Go to pending view (spinner) while document is still being processed
 * 2. success -- Create/submit/poll for a document verification
 * 3. failed -- Show failed screen if document has "errored" status so individual can retry
 */
export function createDocumentGenericWithVerification({
  stepName,
  documentsToVerify,
  documentNumber,
  history,
  fileToUpload,
  t,
  forcedStatus,
}: CreateDocumentGenericWithVerificationArgs): ThunkAction<Promise<void>, RootState, void, Action> {
  return async (dispatch: ThunkDispatch<RootState, void, Action>) => {
    const documentConfig = documentsToVerify[documentNumber];
    const documentPayload: ApiSingletonPayload<
      CreateDocumentGenericAttributes,
      ApiModel.DocumentGeneric
    > = {
      type: ApiModel.DocumentGeneric,
      attributes: {
        kind: documentConfig.kind,
        files: [dataUrlToFile(fileToUpload)],
      },
    };

    // Refresh waiting network status before starting attempt
    dispatch(setUploadProgress(fileToUpload, 0));

    // Update includes a large image upload, so use retries
    const onUploadProgress = (event: AxiosProgressEvent) => {
      dispatch(setUploadProgress(fileToUpload, event.progress ?? 0));
    };
    const startTime = new Date().getTime();

    let documentToSubmit: DocumentGeneric;
    let attempt;
    for (attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        documentToSubmit = (
          await SessionClientVerify.postResourceCreate<DocumentGeneric>(documentPayload, {
            contentType: 'multipart/form-data',
            onUploadProgress,
          })
        ).data;
        break;
      } catch (err) {
        if (err instanceof ApiResponseError) {
          // Do not retry on the below error responses.
          switch (err.status) {
            case 502:
              // NOTE: 502 BAD GATEWAY results currently results from our backend's Puma configuration
              // having a timeout of 60 seconds. If an upload takes longer than 60 seconds, it times out,
              // nginx gets a status 408, and returns a status 502 to the client.
              // In this case, notify the user of connection trouble, so they can get better network
              // speed before retrying.
              dispatch(showMessage(t('errors.network-upload')));
              return;
            case 413: // Payload Too Large
              dispatch(
                showMessage(
                  IMAGE_TYPES.includes(fileToUpload.fileType)
                    ? t('errors.image-upload-too-large')
                    : t('errors.file-upload-too-large'),
                ),
              );
              return;
            case 422: // Unprocessable Entity
              dispatch(showMessage(t('documents.prompt.failed-select-image')));
              return;
          }
        } else if (!(err instanceof ApiNetworkError)) {
          captureException(err, 'error', { attempt });
        }

        if (attempt === MAX_RETRIES - 1) {
          EventsClient.logAmplitude('document.submit.failed', {
            duration: new Date().getTime() - startTime,
          });
          throw err;
        }
      }
    }

    const documentSubmitted = (
      await SessionClientVerify.postResourceAction<DocumentGeneric>(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        { id: documentToSubmit.id, type: documentToSubmit.type },
        'submit',
      )
    ).data;

    EventsClient.logAmplitude('document.submit.completed', {
      duration: new Date().getTime() - startTime,
      attempts: attempt + 1,
    });

    // Update document status in store
    dispatch(updateDocumentAtIndex(documentSubmitted, documentNumber));

    await dispatch(
      pollDocumentAndSubmitVerification({
        document: documentSubmitted,
        stepName,
        documentsToVerify,
        documentNumber,
        history,
        forcedStatus,
        attemptsLeft: POLLS_BEFORE_REDIRECT,
      }),
    );

    // Inquiry 2.0 doesn't ever submit >1 file. We clear the file here to keep
    // things simple (so the uploads array only ever has 1 item at most).
    dispatch(clearUpload(fileToUpload));
  };
}

interface PollDocumentAndSubmitVerificationArgs {
  document: DocumentGeneric;
  stepName: string;
  documentsToVerify: DocumentConfig[];
  documentNumber: number;
  history: History;
  forcedStatus?: VerificationStatus;
  attemptsLeft?: number;
}

export function pollDocumentAndSubmitVerification({
  document,
  stepName,
  documentsToVerify,
  documentNumber,
  history,
  forcedStatus,
  attemptsLeft = MAX_DOCUMENT_POLLS,
}: PollDocumentAndSubmitVerificationArgs): ThunkAction<Promise<void>, RootState, void, Action> {
  return (dispatch: ThunkDispatch<RootState, void, Action>) =>
    dispatch(
      pollDocument({
        document,
        documentsToVerify,
        documentNumber,
        history,
        attemptsLeft,
        onSuccess: async (response: ApiResponse<DocumentGeneric>) => {
          const handleFailed = (verificationResponse: ApiResponse<VerificationDocument>) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const inquiry = verificationResponse.included.find(
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              (resource) => resource.id === verificationResponse.data.relationships.inquiry.data.id,
            ) as Inquiry;

            // NB: If the inquiry's next step is still documents upload we assume that the template
            // has not defined a failed step. If that's the case route to the fallback failed screen.
            // use push instead of replace here, because pollVerification already clears the history stack
            // and calling replace on top of an empty stack leads to a bad state where
            // the next push event replaces the current failed route
            if (inquiry.attributes['next-step'].name === stepName) {
              dispatch(saveInquiry(inquiry));
              dispatch(push(getDocumentStepUri(documentNumber, DocumentStepAction.Failed)));
            } else {
              dispatch(routeToNextStep(inquiry));
            }
          };

          const handlePassed = (verificationResponse: ApiResponse<VerificationDocument>) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const document = verificationResponse.included.find(
              (resource) =>
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                resource.id === verificationResponse.data.relationships.document.data.id,
            ) as DocumentGeneric;
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const inquiry = verificationResponse.included.find(
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              (resource) => resource.id === verificationResponse.data.relationships.inquiry.data.id,
            ) as Inquiry;

            dispatch(updateDocumentAtIndex(document, documentNumber));
            dispatch(routeToNextStepFromDocument(inquiry, documentsToVerify, documentNumber));
          };

          await dispatch(
            verificationDocumentSubmit({
              documentId: response.data.id,
              history,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              forcedStatus,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              handleFailed,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              handlePassed,
              // NB: Do not pass a handleIncomplete callback and provide a large number of attempts
              // since we should already be on the pending screen after document submission. Do not
              // redirect to another pending screen for the verification for a better UX.
              totalAttempts: MAX_VERIFICATION_POLLS,
            }),
          );
        },
        onIncomplete: () => {
          // When going to the pending screen, do not allow one to go back to previous screen by
          // clearing history.
          clearMemoryHistory(history as MemoryHistory);
          dispatch(push(getDocumentStepUri(documentNumber, DocumentStepAction.Pending)));
        },
      }),
    );
}

interface PollDocumentArgs {
  document: DocumentGeneric;
  documentsToVerify: DocumentConfig[];
  documentNumber: number;
  history: History;
  onSuccess?: (response: ApiResponse<DocumentGeneric>) => Promise<void>;
  onIncomplete?: () => void;
  attemptsLeft?: number;
}

/**
 * This function is for polling on submitted document.
 * On `processed`, it will always route based on the inquiry in the response.
 * On `errored` or if out of attempts, it will use the callbacks.
 *
 * If the corresponding fallback wasn't expected, this will error.
 * If an ApiResponseError is hit, this will also error.
 */
export function pollDocument({
  document,
  documentsToVerify,
  documentNumber,
  history,
  onSuccess,
  onIncomplete,
  attemptsLeft = MAX_DOCUMENT_POLLS,
}: PollDocumentArgs): ThunkAction<Promise<void>, RootState, void, Action> {
  return (dispatch: ThunkDispatch<RootState, void, Action>) => {
    return new Promise<void>((resolve, reject) => {
      const incompleteCallback = () => {
        if (onIncomplete) {
          onIncomplete();
          resolve();
        } else {
          reject();
        }
      };

      const passedCallback = async (response: ApiResponse<DocumentGeneric>) => {
        clearMemoryHistory(history as MemoryHistory);
        // Update document status in store
        dispatch(updateDocumentAtIndex(response.data, documentNumber));
        if (onSuccess) {
          await onSuccess(response);
        } else {
          // Route to next step if no callback provided
          dispatch(
            routeToNextStepFromDocument(
              getInquiryFromResponse(response),
              documentsToVerify,
              documentNumber,
            ),
          );
        }
        resolve();
      };

      const failedCallback = (response: ApiResponse<DocumentGeneric>) => {
        clearMemoryHistory(history as MemoryHistory);
        // Update document status in store
        dispatch(updateDocumentAtIndex(response.data, documentNumber));
        // Update inquiry with latest state
        dispatch(saveInquiry(getInquiryFromResponse(response)));
        resolve();
        dispatch(push(getDocumentStepUri(documentNumber, DocumentStepAction.Failed)));
      };

      const repollCallback = (document: DocumentGeneric, currAttemptsLeft: number) => {
        if (currAttemptsLeft <= 0) {
          incompleteCallback();
        } else {
          setTimeout(() => {
            pollDocumentProcessHelper(
              document,
              currAttemptsLeft - 1,
              repollCallback,
              reject,
              passedCallback,
              failedCallback,
            );
          }, POLL_INTERVAL);
        }
      };

      repollCallback(document, attemptsLeft);
    });
  };
}

async function pollDocumentProcessHelper(
  document: DocumentGeneric,
  attemptsLeft: number,
  repollCallback: (document: DocumentGeneric, currAttemptsLeft: number) => void,
  errorCallback: (reason?: Error) => void,
  passedCallback: (response: ApiResponse<DocumentGeneric>) => void,
  failedCallback: (response: ApiResponse<DocumentGeneric>) => void,
): Promise<void> {
  try {
    const documentResponse = await SessionClientVerify.getResource<DocumentGeneric>(
      ApiEndpoint.DocumentGeneric,
      document.id,
    );
    const newDocument = documentResponse.data;

    switch (newDocument.attributes.status) {
      case DocumentStatus.Initiated:
      case DocumentStatus.Submitted:
        repollCallback(newDocument, attemptsLeft);
        return;

      case DocumentStatus.Processed:
        passedCallback(documentResponse);
        return;

      case DocumentStatus.Errored:
        failedCallback(documentResponse);
        return;

      default:
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        assertNever(newDocument.attributes.status);
    }
  } catch (err) {
    if (
      // Continue polling through network and intermittent errors
      err instanceof ApiNetworkError ||
      (err instanceof ApiResponseError && err.status === 502)
    ) {
      repollCallback(document, attemptsLeft);
    } else {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCallback(err);
    }
  }
}

function getInquiryFromResponse(documentResponse: ApiResponse<DocumentGeneric>): Inquiry {
  const document = documentResponse.data;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const inquiry = documentResponse.included.find(
    (res) => res.id === document.relationships.inquiry?.data?.id,
  ) as Inquiry;

  return inquiry;
}
